<script setup lang="ts">
import { ref, computed } from 'vue';
import { useUxData } from '@/stores/ux-data';
import ElementGrid from '@/components/data/grid/ElementGrid.vue';
import AddAction from '@/components/atoms/swade/AddAction.vue';
import { useDoubleClick } from '@/composables/useDoubleClick';
import { useOffcanvas } from '@/composables/useOffcanvas';
import type { SettingElementInterface, SwadeEdgeInterface } from '@/types/items';
import type { ColumnConfig, ModifierConfig } from '@/types/browser';

const props = withDefaults(
  defineProps<{
    placement?: 'start' | 'bottom' | 'end';
    inline?: boolean;
    detailClass?: string;
    closeOnAdd?: boolean;
    dataItems: SettingElementInterface[];
    columns: ColumnConfig[];
    title: string;
    selectedElement?: SettingElementInterface | null;
    isAdded: (element: SettingElementInterface) => boolean;
    canAdd: (element: SettingElementInterface) => boolean;
    loading?: boolean;
    // Lights up the FilterBar's "Clear filters" button and the mobile FilterToggle.
    // Consumers own their filter state, so they own this flag too.
    filtersActive?: boolean;
    metadata?: {
      total: number | null;
      start: number | null;
      end: number | null;
    };
    links?: {
      next: string | null;
      previous: string | null;
      current: string | null;
    };
    modifierConfig?: ModifierConfig;
  }>(),
  {
    placement: 'end',
    inline: false,
    detailClass: '',
    closeOnAdd: false,
    selectedElement: null,
    loading: false,
    filtersActive: false,
    metadata: () => ({ total: null, start: null, end: null }),
    links: () => ({ next: null, previous: null, current: null }),
  },
);

// An element as the details panel reads it. `description` is declared on most of the concrete
// element types and deliberately not on the shared base, so the panel takes this partial view of
// whichever element it is handed. One without a description still falls through to its summary.
type DescribedElement = SettingElementInterface & { description?: string | null };

const emit = defineEmits<{
  add: [element: SettingElementInterface, modifiers: Record<string, string>];
  select: [element: SettingElementInterface];
  search: [event: any];
  next: [];
  previous: [];
  clear: [];
  open: [];
  close: [];
}>();

const ux = useUxData();
const browserOffcanvas = ref<HTMLElement | null>(null);

const firstColumnField = computed(() => props.columns[0]?.field ?? '');

const onAddClick = (element: SettingElementInterface, modifiers: Record<string, string> = {}) => {
  emit('add', element, modifiers);
  if (props.closeOnAdd) hide();
};

const { onClick: onRowClick } = useDoubleClick(
  (element: SettingElementInterface) => {
    if (props.canAdd(element)) onAddClick(element, {});
  },
  {
    onSingleClick: (element: SettingElementInterface) => {
      emit('select', element);
    },
  },
);

const onSelect = (element: SettingElementInterface) => onRowClick(element);

const onSearch = (event: any) => emit('search', event);
const onNext = () => emit('next');
const onPrevious = () => emit('previous');
const onClear = () => emit('clear');

const grid = ref<InstanceType<typeof ElementGrid> | null>(null);
const { show: showPanel, hide } = useOffcanvas(browserOffcanvas, {
  onOpen: () => emit('open'),
  onClose: () => emit('close'),
});

// A browser opened on demand starts fresh: its host asks for an unfiltered first page with no
// keyword (useElementBrowser.open). The grid's search box is not bound to that keyword, so it is
// emptied here, or it would show the last text typed over that unfiltered page. Not for the inline
// form: that is a guided step's own list, a drawer only on a small screen, and its keyword is the
// step's, which opening the drawer does not clear.
const show = () => {
  if (!props.inline) grid.value?.resetSearch();
  showPanel();
};

// Puts back a browser its host only hid for a moment, with the search box as it was left: the
// host has kept its keyword and its list too.
const resume = () => showPanel();

defineExpose({ show, hide, resume });
</script>

<template>
  <div
    ref="browserOffcanvas"
    class="element-browser browser-offcanvas"
    :class="[inline ? 'offcanvas-lg' : 'offcanvas', `offcanvas-${placement}`]"
    tabindex="-1"
  >
    <div class="offcanvas-header" :class="{ 'd-lg-none': inline }">
      <h5 class="offcanvas-title">{{ title }}</h5>
      <slot name="actions"></slot>
      <button
        type="button"
        class="btn-close"
        :aria-label="(ux.labels as any)?.actions?.close ?? 'Close'"
        @click="hide"
      ></button>
    </div>
    <div class="offcanvas-body">
      <ElementGrid
        ref="grid"
        :data-items="dataItems"
        :columns="columns"
        :selected-element="selectedElement"
        :loading="loading"
        :metadata="metadata"
        :links="links"
        :filters-active="filtersActive"
        :show-details-panel="true"
        :page-size-control="true"
        :detail-class="detailClass"
        :search-placeholder="(ux.labels as any)?.placeholders?.search ?? 'Search...'"
        @select="onSelect"
        @search="onSearch"
        @next="onNext"
        @previous="onPrevious"
        @clear="onClear"
      >
        <!-- Inject AddAction into the first column cell -->
        <template
          v-if="firstColumnField"
          #[`cell-${firstColumnField}`]="{ element }"
        >
          <span v-if="canAdd(element)" class="d-lg-inline-block d-none me-1">
            <AddAction
              :allow-multiple="isAdded(element) && ((element as SwadeEdgeInterface).allowMultiple ?? false)"
              :element="element"
              :modifier-config="modifierConfig"
              @add="onAddClick"
            />
          </span>
          <span v-else-if="isAdded(element)" class="d-lg-inline-block d-none fa-light fa-check text-success me-1"></span>
          <span>{{ element.name ?? '' }}</span>
          <span v-if="canAdd(element)" class="d-lg-none d-inline-block me-1">
            <AddAction
                :allow-multiple="isAdded(element) && ((element as SwadeEdgeInterface).allowMultiple ?? false)"
                :element="element"
                :modifier-config="modifierConfig"
                @add="onAddClick"
            />
          </span>
          <span v-else-if="isAdded(element)" class="d-lg-none d-inline-block fa-light fa-check text-success me-1"></span>
        </template>

        <!-- Forward consumer's #filters slot only when provided, so Finders that
             pass no filters don't get an empty drawer/toggle. -->
        <template v-if="$slots.filters" #filters>
          <slot name="filters"></slot>
        </template>

        <!-- Forward consumer's #details slot, append primary AddAction button -->
        <template #details="{ element }">
          <slot name="details" :element="element">
            <div v-if="element.permissions?.edit && (element as DescribedElement).description" class="small mb-3" v-html="(element as DescribedElement).description"></div>
            <div v-else-if="element.summary" class="small mb-3" v-html="element.summary"></div>
            <div v-else class="py-3 px-5 text-center">
              <span class="fa-light fa-fw fa-circle-notch fa-spin"></span>
            </div>
          </slot>
          <AddAction
            v-if="canAdd(element)"
            :element="element"
            :modifier-config="modifierConfig"
            :drop-down-icon="true"
            variant="primary"
            :block="true"
            :label="(ux.labels as any)?.actions?.add ?? 'Add'"
            @add="onAddClick"
          />
        </template>
      </ElementGrid>
    </div>
  </div>
</template>
